/**
 * The colour parameters for the Cancellations charts.
 *
 * `ChartsService` supplies chart *chrome* only — axis, tooltip and single-series
 * colours — so a multi-series palette has to be defined here. These values are
 * not chosen by eye: every set below was run through the data-viz validator
 * (lightness band, chroma floor, CVD separation, normal-vision floor, contrast)
 * against this dashboard's own panel surfaces, `#ffffff` light and the
 * `.admin-stats-panel` gradient's mid-tone `#15151e` dark.
 *
 * If you change a hex, re-run the validator. Two of the results below are
 * conditional passes and the conditions are load-bearing, not style notes.
 */

export type CancellationOutcomeSeriesKey =
  | 'cancelled'
  | 'kept'
  | 'paused'
  | 'started';

/**
 * Outcome series, in the order they are stacked and legended.
 *
 * The order is the accessibility mechanism, not a preference. Stacked segments
 * and legend entries are validated on the *adjacent* pairlist, so which colour
 * sits next to which is what the checks measure. Red beside yellow failed the
 * dark-mode normal-vision floor (ΔE 13.0, floor 15); moving green between them
 * clears both modes. Reordering these without re-validating will silently
 * reintroduce that.
 *
 * Worst adjacent CVD ΔE is 7.2 light / 6.9 dark — inside the 6–8 band, which is
 * permitted *only* with secondary encoding. That is why every chart using these
 * ships a legend, direct value labels, and 2px surface gaps between segments,
 * and why the Responses tab exists as the table view. Do not remove those.
 */
export const OUTCOME_SERIES: {
  key: CancellationOutcomeSeriesKey;
  /**
   * The same series under the name the per-reason breakdown uses. The reason
   * stats call the abandoned bucket `abandoned`; the timeline calls it
   * `started`, after the outcome it is still sitting on.
   */
  statKey: 'cancelled' | 'kept' | 'paused' | 'abandoned';
  label: string;
  light: string;
  dark: string;
}[] = [
  { key: 'cancelled', statKey: 'cancelled', label: 'Cancelled', light: '#e34948', dark: '#e66767' },
  { key: 'kept', statKey: 'kept', label: 'Saved', light: '#008300', dark: '#008300' },
  { key: 'paused', statKey: 'paused', label: 'Paused', light: '#eda100', dark: '#c98500' },
  { key: 'started', statKey: 'abandoned', label: 'Abandoned', light: '#2a78d6', dark: '#3987e5' },
];

/**
 * Return likelihood, strongest first.
 *
 * An ordered scale, so it is one hue stepped by lightness rather than four
 * identities — "Maybe" is nearer "Very likely" than "No" is, and a categorical
 * palette would hide that. Validated with `--ordinal`: monotone lightness,
 * visible step gaps, and the step nearest the surface still clears 2:1.
 *
 * The dark column is not an inversion of the light one: on a dark ground the
 * strongest step has to be the *lightest*, so the ramp runs the other way.
 */
export const RETURN_RAMP: { id: string; light: string; dark: string }[] = [
  { id: 'very_likely', light: '#1c5cab', dark: '#9ec5f4' },
  { id: 'maybe', light: '#2a78d6', dark: '#6da7ec' },
  { id: 'unlikely', light: '#5598e7', dark: '#3987e5' },
  { id: 'no', light: '#86b6ef', dark: '#256abf' },
];

/** Single-series bars (follow-up counts) need one colour, and no legend. */
export const SINGLE_SERIES = { light: '#2a78d6', dark: '#3987e5' };

/** Recessive chrome: gridlines and axes sit one shade off the surface. */
export const CHART_CHROME = {
  light: { grid: '#e1e0d9', axis: '#c3c2b7', muted: '#898781', surface: '#ffffff' },
  dark: { grid: '#2c2c2a', axis: '#383835', muted: '#898781', surface: '#15151e' },
};

export function outcomeSeriesColor(
  key: CancellationOutcomeSeriesKey,
  isDark: boolean,
): string {
  const series = OUTCOME_SERIES.find((entry) => entry.key === key);
  if (!series) return isDark ? SINGLE_SERIES.dark : SINGLE_SERIES.light;
  return isDark ? series.dark : series.light;
}

export function returnRampColor(id: string, isDark: boolean): string {
  const step = RETURN_RAMP.find((entry) => entry.id === id);
  if (!step) return isDark ? SINGLE_SERIES.dark : SINGLE_SERIES.light;
  return isDark ? step.dark : step.light;
}
